import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "../../lib/axios";
import {
  getDesigningDisplayPrice,
  normalizeDesigning,
} from "../../utils/designing";

const INITIAL_PAGINATION = {
  total: 0,
  page: 1,
  pages: 1,
  limit: 12,
};

const getSerializedFilters = (filters) => JSON.stringify(filters || {});

export const useCatalogListingPage = (config) => {
  const location = useLocation();
  const { highlightCard, scrollToCard } = location.state || {};
  const targetId =
    scrollToCard && highlightCard
      ? `${config.cardIdPrefix}-${highlightCard}`
      : location.hash
        ? location.hash.slice(1)
        : "";

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("popularity");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [hasMore, setHasMore] = useState(true);
  const [filterOptions, setFilterOptions] = useState({});

  const resetResults = useCallback(() => {
    setItems([]);
    setHasMore(true);
    setPagination((prev) => ({
      ...prev,
      total: 0,
      page: 1,
      pages: 1,
    }));
  }, []);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      const nextSearchTerm = searchInput.trim();

      if (nextSearchTerm === searchTerm) {
        return;
      }

      setSearchTerm(nextSearchTerm);
      resetResults();
    }, 300);

    return () => window.clearTimeout(debounceTimer);
  }, [searchInput, searchTerm, resetResults]);

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      setError(null);

      try {
        const { params, isPriceSort } = config.buildQueryParams({
          page: pagination.page,
          limit: pagination.limit,
          sortBy,
          searchTerm,
          selectedFilters,
        });

        const response = await axios.get(`${config.endpoint}?${params.toString()}`);
        const data = response.data;

        if (response.status !== 200) {
          setError(data.message || "Failed to fetch items");
          return;
        }

        const results = Array.isArray(data.docs) ? data.docs : [];
        const normalizedItems = results.map(normalizeDesigning);
        const nextItems = isPriceSort
          ? [...normalizedItems].sort((a, b) => {
              const priceA = getDesigningDisplayPrice(a);
              const priceB = getDesigningDisplayPrice(b);

              return sortBy === "price_high"
                ? priceB - priceA
                : priceA - priceB;
            })
          : normalizedItems;

        if (isPriceSort || pagination.page === 1) {
          setItems(nextItems);
        } else {
          setItems((previousItems) => [...previousItems, ...nextItems]);
        }

        setPagination((prev) => ({
          ...prev,
          total: data.totalDocs,
          pages: isPriceSort ? 1 : data.totalPages,
        }));
        setHasMore(isPriceSort ? false : pagination.page < data.totalPages);
      } catch (fetchError) {
        setError(config.fetchErrorMessage || "Failed to fetch items");
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [
    config,
    pagination.page,
    pagination.limit,
    searchTerm,
    sortBy,
    selectedFilters,
  ]);

  useEffect(() => {
    if (!targetId) return;

    const scrollToTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const rafId = window.requestAnimationFrame(scrollToTarget);
    const timeoutIds = [160, 420, 760].map((delay) =>
      window.setTimeout(scrollToTarget, delay),
    );

    return () => {
      window.cancelAnimationFrame(rafId);
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [targetId, loading, items.length]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (typeof config.getFilterOptions === 'function') {
        const isAsync = config.getFilterOptions.constructor.name === 'AsyncFunction';
        if (isAsync) {
          const options = await config.getFilterOptions();
          setFilterOptions(options);
        } else {
          setFilterOptions(config.getFilterOptions(items));
        }
      }
    };
    fetchFilterOptions();
  }, [config, items]);

  const visibleItems = useMemo(
    () => config.filterVisibleItems(items, selectedFilters),
    [config, items, selectedFilters],
  );

  const handleSortClick = (value) => {
    if (value === sortBy) {
      setShowSortDropdown(false);
      return;
    }

    setSortBy(value);
    setShowSortDropdown(false);
    resetResults();
  };

  const handleFilterChange = (filterType, value) => {
    const nextFilters = config.updateFilters(selectedFilters, filterType, value);

    if (getSerializedFilters(nextFilters) === getSerializedFilters(selectedFilters)) {
      return;
    }

    setSelectedFilters(nextFilters);
    resetResults();
  };

  return {
    category: config.category,
    clientsCategory: config.clientsCategory,
    videosCategory: config.videosCategory,
    faqItems: config.faqItems,
    reachOut: config.reachOut,
    searchPlaceholder: config.searchPlaceholder,
    emptyMessage: config.emptyMessage(searchTerm),
    showFilterOptions: config.showFilterOptions,
    loading,
    items,
    error,
    sortBy,
    searchInput,
    showSortDropdown,
    selectedFilters,
    pagination,
    hasMore,
    visibleItems,
    totalVisibleItems: visibleItems.length,
    filterOptions,
    setSearchInput,
    setShowSortDropdown,
    handleSortClick,
    handleFilterChange,
    loadMore: () =>
      setPagination((prev) => ({ ...prev, page: prev.page + 1 })),
    getCardId: (item, index) =>
      `${config.cardIdPrefix}-${item.slug || item.type || index}`,
  };
};
